import { useEffect, useRef } from "react";

/**
 * Closes a popup on Escape.
 *
 * The listener is registered once and reads `closeAction` through a ref, rather than depending on
 * it. Both halves matter:
 *
 * - The old version captured `closeAction` in a `useEffect` with `[]` deps, so a popup whose
 *   close handler changed over its life kept calling the first one it ever had -- usually a stale
 *   closure over the first render's state, which is a dead Escape key with no error to show for
 *   it. See `docs/solutions/components/popup-esc-handler-stale-closure.md`.
 * - Depending on `[closeAction]` instead would fix the staleness and add a new cost: most callers
 *   pass an inline arrow (`useHandleEsc(() => onClose())`), whose identity changes every render, so
 *   the window listener would be torn down and re-added on every render of every open popup.
 *
 * The ref gives the current handler without either problem.
 */
export const useHandleEsc = (closeAction: any) => {
  const closeActionRef = useRef(closeAction);
  closeActionRef.current = closeAction;

  useEffect(() => {
    const onKeyDown = (event: any) => {
      // Optional call, matching `PopupSimple`, which types `closeAction` as optional and guards
      // both the X and the Cancel link with `closeAction?.(false)`. Escape was the one path that
      // did not, so `closeAction={busy ? undefined : onCancel}` -- the obvious way to disable
      // closing -- threw a TypeError instead of doing nothing. Reading the ref above makes that
      // spelling actually reach the listener, so the guard has to be here.
      if (event.keyCode === 27) closeActionRef.current?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
};
